import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import { Upload, Trash2, Save, Building2, Printer, UserPlus, Users, Copy, Download, UserMinus, Send, RefreshCw, Eye, MousePointerClick } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { LogoMark } from "@/components/Logo";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function CompanyProfile() {
  const { user } = useAuth();
  const [form, setForm] = useState({
    company_name: "", company_email: "", company_phone: "",
    company_contact_name: "", company_address: "", labels_enabled: true,
    brand_color: "",
  });
  const [logoUrl, setLogoUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [creatingInvite, setCreatingInvite] = useState(false);
  const [downloadingBackup, setDownloadingBackup] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ invitee_name: "", invitee_email: "" });
  const fileRef = useRef(null);

  const loadTeam = async () => {
    try {
      const [m, i] = await Promise.all([api.get("/team/members"), api.get("/team/invites")]);
      setMembers(m.data.members || []);
      setInvites(i.data || []);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("team load failed", err);
    }
  };

  const load = async () => {
    const { data } = await api.get("/company");
    setForm({
      company_name: data.company_name || "",
      company_email: data.company_email || "",
      company_phone: data.company_phone || "",
      company_contact_name: data.company_contact_name || "",
      company_address: data.company_address || "",
      labels_enabled: data.labels_enabled !== false,
      brand_color: data.brand_color || "",
    });
    setLogoUrl(data.company_logo_url || "");
  };
  useEffect(() => { load(); loadTeam(); }, []);

  const createInvite = async () => {
    setCreatingInvite(true);
    try {
      const { data } = await api.post("/team/invite", {
        ttl_hours: 168,
        invitee_name: inviteForm.invitee_name || "",
        invitee_email: inviteForm.invitee_email || null,
      });
      navigator.clipboard?.writeText(data.invite_url).catch(() => {});
      if (data.notified) {
        toast.success(`Invitation emailed to ${data.invitee_email}`, { description: "Link also copied to your clipboard" });
      } else if (data.invitee_email) {
        toast.warning("Invite created — email delivery failed", { description: `Share the link manually: ${data.invite_url}` });
      } else {
        toast.success("Invite link copied", { description: data.invite_url });
      }
      setInviteOpen(false);
      setInviteForm({ invitee_name: "", invitee_email: "" });
      loadTeam();
    } catch (err) {
      toast.error("Could not create invite", { description: formatApiError(err.response?.data?.detail) });
    } finally {
      setCreatingInvite(false);
    }
  };

  const revokeInvite = async (code) => {
    await api.delete(`/team/invite/${code}`);
    toast.message("Invite revoked");
    loadTeam();
  };

  const resendInvite = async (inv) => {
    if (!inv.invitee_email) {
      toast.error("This invite has no email address on file");
      return;
    }
    try {
      await api.post(`/team/invite/${inv.code}/resend`);
      toast.success(`Reminder emailed to ${inv.invitee_email}`);
      loadTeam();
    } catch (err) {
      toast.error("Resend failed", { description: formatApiError(err.response?.data?.detail) });
    }
  };

  const removeMember = async (m) => {
    if (!window.confirm(`Remove ${m.name || m.email} from your team? They will lose access to all shared requests, suppliers and couriers immediately.`)) return;
    try {
      await api.delete(`/team/members/${m.user_id}`);
      toast.success(`${m.name || m.email} removed`);
      loadTeam();
    } catch (err) {
      toast.error("Could not remove", { description: formatApiError(err.response?.data?.detail) });
    }
  };

  const copyLink = (code) => {
    const url = `${window.location.origin}/register?invite=${code}`;
    navigator.clipboard?.writeText(url).catch(() => {});
    toast.success("Link copied");
  };

  const downloadBackup = async () => {
    setDownloadingBackup(true);
    try {
      const { data } = await api.get("/backup");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `out-and-in-backup-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Backup downloaded", { description: `${data.requests?.length || 0} requests, ${data.suppliers?.length || 0} suppliers, ${data.couriers?.length || 0} couriers` });
    } catch (err) {
      toast.error("Backup failed", { description: formatApiError(err.response?.data?.detail) });
    } finally {
      setDownloadingBackup(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/company", {
        ...form,
        company_email: form.company_email || null,
        brand_color: form.brand_color || null,
      });
      toast.success("Company profile saved");
    } catch (err) {
      toast.error("Save failed", { description: formatApiError(err.response?.data?.detail) });
    } finally {
      setSaving(false);
    }
  };

  const onPickFile = () => fileRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 1_000_000) {
      toast.error("Logo too large", { description: "Max 1MB. Try compressing your image." });
      return;
    }
    setUploading(true);
    try {
      const b64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const { data } = await api.post("/company/logo", {
        logo_base64: b64,
        content_type: file.type || "image/png",
      });
      setLogoUrl(data.company_logo_url);
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error("Upload failed", { description: formatApiError(err.response?.data?.detail) });
    } finally {
      setUploading(false);
    }
  };

  const removeLogo = async () => {
    await api.delete("/company/logo");
    setLogoUrl("");
    toast.message("Logo removed");
  };

  return (
    <div className="space-y-8 max-w-4xl">
      <div className="flex items-center gap-4 flex-wrap">
        <div className="border-2 border-foreground p-2">
          <LogoMark size={40} className="text-foreground" />
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground font-bold">
            Out <span className="text-orange-600">&amp;</span> In &middot; Your Account
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter mt-1">Company Profile</h1>
        </div>
      </div>

      <section className="border-2 border-border bg-card">
        <div className="px-6 py-3 border-b-2 border-border text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">
          Company Logo
        </div>
        <div className="p-6 flex items-start gap-6 flex-wrap">
          <div className="border-2 border-border bg-muted w-40 h-40 flex items-center justify-center overflow-hidden">
            {logoUrl ? (
              <img data-testid="company-logo-preview" src={`${BACKEND}${logoUrl}`} alt="Company logo" className="max-w-full max-h-full object-contain" />
            ) : (
              <Building2 className="h-14 w-14 text-muted-foreground opacity-40" />
            )}
          </div>
          <div className="space-y-3 flex-1 min-w-[240px]">
            <p className="text-sm text-muted-foreground max-w-md">
              Upload a PNG, JPG, SVG or WebP (max 1MB). This appears in your dashboard and
              on requests you file so recipients can recognise your brand.
            </p>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp,image/gif" onChange={onFile} className="hidden" data-testid="logo-file-input" />
            <div className="flex gap-3 flex-wrap">
              <Button type="button" onClick={onPickFile} disabled={uploading} data-testid="upload-logo-button" className="rounded-none bg-orange-600 hover:bg-orange-700 h-10 uppercase tracking-[0.15em] text-xs font-bold">
                <Upload className="h-3.5 w-3.5 mr-2" /> {uploading ? "Uploading..." : logoUrl ? "Replace Logo" : "Upload Logo"}
              </Button>
              {logoUrl && (
                <Button type="button" variant="outline" onClick={removeLogo} data-testid="remove-logo-button" className="rounded-none border-2 h-10 uppercase tracking-[0.15em] text-xs font-bold hover:border-red-600 hover:text-red-600">
                  <Trash2 className="h-3.5 w-3.5 mr-2" /> Remove
                </Button>
              )}
            </div>
          </div>
        </div>
      </section>

      <form onSubmit={submit} className="border-2 border-border bg-card">
        <div className="px-6 py-3 border-b-2 border-border text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">
          Company Details
        </div>
        <div className="p-6 space-y-4">
          <div className="space-y-2">
            <Label className="text-[10px] uppercase tracking-[0.2em] font-bold">Company name *</Label>
            <Input data-testid="company-name" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} required className="h-10 rounded-none border-2" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-[10px] uppercase tracking-[0.2em] font-bold">Contact name</Label>
              <Input data-testid="company-contact" value={form.company_contact_name} onChange={(e) => setForm({ ...form, company_contact_name: e.target.value })} className="h-10 rounded-none border-2" />
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] uppercase tracking-[0.2em] font-bold">Phone</Label>
              <Input data-testid="company-phone" value={form.company_phone} onChange={(e) => setForm({ ...form, company_phone: e.target.value })} className="h-10 rounded-none border-2" />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-[10px] uppercase tracking-[0.2em] font-bold">Company email</Label>
            <Input type="email" data-testid="company-email" value={form.company_email} onChange={(e) => setForm({ ...form, company_email: e.target.value })} className="h-10 rounded-none border-2" placeholder="Where couriers reply to" />
          </div>
          <div className="space-y-2">
            <Label className="text-[10px] uppercase tracking-[0.2em] font-bold">Company address</Label>
            <Textarea data-testid="company-address" value={form.company_address} onChange={(e) => setForm({ ...form, company_address: e.target.value })} rows={3} className="rounded-none border-2" placeholder="Street, city, postcode..." />
            <p className="text-xs text-muted-foreground">Tap "Use my company" on the New Request form to auto-fill this as the pickup or delivery address.</p>
          </div>
          <div className="space-y-2 pt-2 border-t-2 border-border">
            <Label className="text-[10px] uppercase tracking-[0.2em] font-bold pt-4 block">Email brand accent</Label>
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  data-testid="brand-color-picker"
                  value={form.brand_color || "#FF5A1F"}
                  onChange={(e) => setForm({ ...form, brand_color: e.target.value.toUpperCase() })}
                  className="h-11 w-14 border-2 border-border rounded-none cursor-pointer bg-transparent p-0"
                  aria-label="Pick brand accent color"
                />
                <Input
                  data-testid="brand-color-hex"
                  value={form.brand_color || ""}
                  onChange={(e) => setForm({ ...form, brand_color: e.target.value })}
                  placeholder="#FF5A1F"
                  maxLength={7}
                  className="h-11 w-32 rounded-none border-2 font-mono uppercase"
                />
                {form.brand_color && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setForm({ ...form, brand_color: "" })}
                    data-testid="brand-color-reset"
                    className="rounded-none border-2 h-11 uppercase tracking-[0.15em] text-[11px] font-bold"
                  >
                    Reset
                  </Button>
                )}
              </div>
              {/* Live mini-preview of the email header strip */}
              <div data-testid="brand-color-preview" className="flex-1 min-w-[260px] max-w-[380px] border-2 border-border">
                <div style={{ background: "#0A0A0A", borderBottom: `2px solid ${form.brand_color || "#FF5A1F"}` }} className="px-3 py-2 flex items-center">
                  <div className="text-white font-black tracking-tight text-sm">
                    OUT <span style={{ color: form.brand_color || "#FF5A1F" }}>&amp;</span> IN
                  </div>
                </div>
                <div style={{ borderBottom: `2px solid ${form.brand_color || "#FF5A1F"}` }} className="px-3 py-2 bg-white">
                  <div style={{ color: form.brand_color || "#FF5A1F" }} className="text-[9px] tracking-[0.25em] uppercase font-bold">Preview · Pickup Request</div>
                  <div className="text-[11px] text-black font-bold mt-0.5">Pickup Scheduled: PR-202602-0001</div>
                </div>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">Tints the header stripe, callout labels and the "Download PDF" button in every email we send on your team's behalf. Leave blank to use the Out &amp; In orange.</p>
          </div>
          <div className="pt-2">
            <Button type="submit" disabled={saving} data-testid="save-company-button" className="rounded-none bg-orange-600 hover:bg-orange-700 h-11 uppercase tracking-[0.15em] text-xs font-bold px-6">
              <Save className="h-3.5 w-3.5 mr-2" /> {saving ? "Saving..." : "Save Company"}
            </Button>
          </div>
        </div>
      </form>

      <section className="border-2 border-border bg-card">
        <div className="px-6 py-3 border-b-2 border-border flex items-center justify-between gap-2 flex-wrap">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground flex items-center gap-2">
            <Users className="h-3.5 w-3.5" /> Team &middot; {members.length} member{members.length === 1 ? "" : "s"}
          </div>
          <Button
            type="button"
            onClick={() => setInviteOpen(true)}
            data-testid="open-invite-dialog-button"
            className="rounded-none bg-orange-600 hover:bg-orange-700 h-9 uppercase tracking-[0.15em] text-xs font-bold"
          >
            <UserPlus className="h-3.5 w-3.5 mr-2" />
            Invite teammate
          </Button>
        </div>
        <div className="p-6 space-y-4">
          <div className="space-y-2">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">Members</div>
            <div className="divide-y-2 divide-border border-2 border-border">
              {members.length === 0 && <div className="p-3 text-sm text-muted-foreground">Just you so far.</div>}
              {members.map((m) => {
                const isOwner = m.user_id === user?.team_id;
                const canRemove = user?.user_id === user?.team_id && m.user_id !== user?.user_id;
                return (
                  <div key={m.user_id} data-testid={`team-member-${m.user_id}`} className="p-3 flex items-center gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-sm truncate">{m.name || m.email}</div>
                      <div className="text-xs text-muted-foreground truncate">{m.email}</div>
                    </div>
                    <span className="text-[10px] uppercase tracking-[0.15em] font-bold px-2 py-1 border border-border">
                      {isOwner ? "Owner" : m.role || "user"}
                    </span>
                    {canRemove && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => removeMember(m)}
                        data-testid={`remove-member-${m.user_id}`}
                        className="rounded-none border-2 h-8 text-xs uppercase tracking-[0.15em] font-bold hover:border-red-600 hover:text-red-600"
                      >
                        <UserMinus className="h-3 w-3 mr-1.5" /> Remove
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          {invites.length > 0 && (
            <div className="space-y-2">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">Open invites</div>
              <div className="divide-y-2 divide-border border-2 border-border">
                {invites.map((inv) => (
                  <div key={inv.code} data-testid={`invite-row-${inv.code}`} className="p-3 flex flex-col gap-3">
                    <div className="flex items-center gap-3 flex-wrap">
                      <code className="font-mono text-sm font-bold px-2 py-1 border-2 border-border">{inv.code}</code>
                      <div className="text-xs min-w-0 flex-1">
                        {inv.invitee_name || inv.invitee_email ? (
                          <>
                            <div className="font-semibold truncate">{inv.invitee_name || inv.invitee_email}</div>
                            <div className="text-muted-foreground truncate">
                              {inv.invitee_email}
                              {inv.notified && <span className="ml-2 text-emerald-600 font-bold uppercase tracking-wider text-[10px]">Emailed</span>}
                            </div>
                          </>
                        ) : (
                          <div className="text-muted-foreground">Open invite (no recipient set)</div>
                        )}
                        <div className="text-muted-foreground text-[11px] mt-0.5">Expires {new Date(inv.expires_at).toLocaleString()}</div>
                      </div>
                      {inv.invitee_email && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => resendInvite(inv)}
                          data-testid={`invite-resend-${inv.code}`}
                          className="rounded-none border-2 h-8 text-xs uppercase tracking-[0.15em] font-bold hover:border-orange-600 hover:text-orange-600"
                        >
                          <RefreshCw className="h-3 w-3 mr-1.5" /> Resend
                        </Button>
                      )}
                      <Button type="button" size="sm" variant="outline" onClick={() => copyLink(inv.code)} className="rounded-none border-2 h-8 text-xs uppercase tracking-[0.15em] font-bold">
                        <Copy className="h-3 w-3 mr-1.5" /> Copy link
                      </Button>
                      <Button type="button" size="sm" variant="outline" onClick={() => revokeInvite(inv.code)} data-testid={`invite-revoke-${inv.code}`} className="rounded-none border-2 h-8 text-xs uppercase tracking-[0.15em] font-bold hover:border-red-600 hover:text-red-600">
                        <Trash2 className="h-3 w-3 mr-1.5" /> Revoke
                      </Button>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-center border-t border-border pt-2" data-testid={`invite-analytics-${inv.code}`}>
                      <div className="border border-border py-1.5">
                        <div className="flex items-center justify-center gap-1 text-[9px] uppercase tracking-[0.2em] font-bold text-muted-foreground">
                          <Eye className="h-2.5 w-2.5" /> Opens
                        </div>
                        <div className="font-mono font-bold text-sm mt-0.5" data-testid={`invite-opens-${inv.code}`}>{inv.opens || 0}</div>
                        {inv.last_opened_at && <div className="text-[9px] text-muted-foreground">{new Date(inv.last_opened_at).toLocaleDateString()}</div>}
                      </div>
                      <div className="border border-border py-1.5">
                        <div className="flex items-center justify-center gap-1 text-[9px] uppercase tracking-[0.2em] font-bold text-muted-foreground">
                          <MousePointerClick className="h-2.5 w-2.5" /> Clicks
                        </div>
                        <div className="font-mono font-bold text-sm mt-0.5" data-testid={`invite-clicks-${inv.code}`}>{inv.clicks || 0}</div>
                        {inv.last_clicked_at && <div className="text-[9px] text-muted-foreground">{new Date(inv.last_clicked_at).toLocaleDateString()}</div>}
                      </div>
                      <div className="border border-border py-1.5">
                        <div className="flex items-center justify-center gap-1 text-[9px] uppercase tracking-[0.2em] font-bold text-muted-foreground">
                          <Send className="h-2.5 w-2.5" /> Resends
                        </div>
                        <div className="font-mono font-bold text-sm mt-0.5" data-testid={`invite-resends-${inv.code}`}>{inv.resend_count || 0}</div>
                        {inv.last_resent_at && <div className="text-[9px] text-muted-foreground">{new Date(inv.last_resent_at).toLocaleDateString()}</div>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="border-2 border-border bg-card">
        <div className="px-6 py-3 border-b-2 border-border text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">
          Backup
        </div>
        <div className="p-6 flex items-start gap-4 flex-wrap">
          <div className="flex items-start gap-3 max-w-xl flex-1 min-w-[240px]">
            <Download className="h-5 w-5 mt-0.5 text-orange-600 shrink-0" />
            <div>
              <div className="font-bold text-sm">Download a full backup</div>
              <p className="text-xs text-muted-foreground mt-1">
                Exports every request, supplier, courier and team member for your account as
                a JSON file. Save it to your own drive for compliance or before archiving old data.
              </p>
            </div>
          </div>
          <Button type="button" onClick={downloadBackup} disabled={downloadingBackup} data-testid="download-backup-button" className="rounded-none bg-foreground text-background hover:bg-foreground/85 h-10 uppercase tracking-[0.15em] text-xs font-bold">
            <Download className="h-3.5 w-3.5 mr-2" />
            {downloadingBackup ? "Preparing..." : "Download JSON"}
          </Button>
        </div>
      </section>

      <section className="border-2 border-border bg-card">
        <div className="px-6 py-3 border-b-2 border-border text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">
          Features
        </div>
        <div className="p-6 flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3 max-w-xl">
            <Printer className="h-5 w-5 mt-0.5 text-orange-600 shrink-0" />
            <div>
              <div className="font-bold text-sm">Package Labels</div>
              <p className="text-xs text-muted-foreground mt-1">
                When enabled, a "Labels" button appears on every request so you can print
                one branded label per package group. Turn this off if your setup does not need
                physical labels.
              </p>
            </div>
          </div>
          <Switch
            data-testid="labels-enabled-toggle"
            checked={!!form.labels_enabled}
            onCheckedChange={async (v) => {
              setForm({ ...form, labels_enabled: v });
              try {
                await api.put("/company", { ...form, labels_enabled: v, company_email: form.company_email || null, brand_color: form.brand_color || null });
                toast.success(v ? "Labels enabled" : "Labels disabled");
              } catch { toast.error("Could not update setting"); }
            }}
          />
        </div>
      </section>

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent data-testid="invite-dialog" className="rounded-none border-2 max-w-md">
          <DialogHeader>
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-orange-600">Team Invite</div>
            <DialogTitle className="font-heading text-2xl font-black tracking-tight">Invite a teammate</DialogTitle>
            <DialogDescription className="text-sm">
              Add their name and email so we can send them a branded invitation with your unique join link.
              Leave the email blank if you just want a shareable link to send yourself.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-[10px] uppercase tracking-[0.2em] font-bold">Teammate name</Label>
              <Input
                data-testid="invitee-name"
                value={inviteForm.invitee_name}
                onChange={(e) => setInviteForm({ ...inviteForm, invitee_name: e.target.value })}
                placeholder="e.g. Jamie Kelly"
                className="h-10 rounded-none border-2 focus-visible:ring-orange-500"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] uppercase tracking-[0.2em] font-bold">Teammate email</Label>
              <Input
                data-testid="invitee-email"
                type="email"
                value={inviteForm.invitee_email}
                onChange={(e) => setInviteForm({ ...inviteForm, invitee_email: e.target.value })}
                placeholder="jamie@company.com"
                className="h-10 rounded-none border-2 focus-visible:ring-orange-500"
              />
              <p className="text-[11px] text-muted-foreground">
                Provide an email and we'll send them the invite instantly. No email? A share link will be copied to your clipboard.
              </p>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button type="button" variant="outline" onClick={() => setInviteOpen(false)} data-testid="cancel-invite" className="rounded-none border-2 h-10 uppercase tracking-[0.15em] text-xs font-bold">
              Cancel
            </Button>
            <Button
              type="button"
              onClick={createInvite}
              disabled={creatingInvite}
              data-testid="send-invite"
              className="rounded-none bg-orange-600 hover:bg-orange-700 h-10 uppercase tracking-[0.15em] text-xs font-bold"
            >
              <Send className="h-3.5 w-3.5 mr-2" />
              {creatingInvite ? "Sending..." : inviteForm.invitee_email ? "Send Invite" : "Create Link"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
